// Define the Reel interface
export interface IReel extends PIXI.Container {
  hasExpandedWild(): boolean;
  expandWild(): Promise<void>;
  getState(): ReelState;
  getIndex(): number;
  getSymbol(index: number): Symbol;
  resetData(): void;
  triggerSpin(): void;
  triggerStop(quickStop: boolean): void;
  setData(reelData: string[]): void;
  startAnticipation(): void;
  stopAnticipation(): void;
  getScattersAmount(): number;
  moveSymbol(symbol: Symbol, row: number, delay: number): Promise<void>;
  removeSymbol(row: number, quickStop: boolean): Promise<void>;
}

import * as PIXI from "pixi.js";
import gsap from "gsap";
import { Symbol } from "../Symbol";
import { GameEngine } from "../server/GameEngine";
import { Panel } from "./Panel";
import { soundManager } from "src/shared/UI/SoundManager";

// Define an enum for the states
export enum ReelState {
  IDLE = "IDLE",
  SPIN = "SPIN",
  STOP = "STOP",
  SPIN_END = "SPIN_END",
}

export class Reel extends PIXI.Container implements IReel {
  private reelData: string[] = [];
  private symbols: Symbol[] = [];
  private currentState: ReelState = ReelState.IDLE;
  private index: number = 0;
  private quickStop: boolean = false;

  constructor(reelData: string[], index: number) {
    super();
    this.index = index;
    this.reelData = reelData;

    this._createSymbols();
  }

  expandWild(): Promise<void> {
    throw new Error("Method not implemented.");
  }

  /**
   * Get the index of this reel.
   * @returns The index of the reel.
   */
  public getIndex(): number {
    return this.index;
  }

  /**
   * Get the reel data.
   * @returns The reel data array.
   */
  public getData(): string[] {
    return this.reelData;
  }

  /**
   * Set the reel data.
   * @param reelData - An array of symbols to populate the reel.
   */
  public setData(reelData: string[]): void {
    this.reelData = reelData;
  }

  /**
   * Create symbols and populate the reel.
   */
  private _createSymbols(): void {
    for (let i = 0; i < this.reelData.length; i++) {
      const symbol: Symbol = Symbol.pool.acquire();
      symbol.setType(this.reelData[i].toString());
      symbol.y = this._getY(i);
      this.addChild(symbol);

      this.symbols.push(symbol);
      symbol.originalParent = this;
      symbol.originalPosition = symbol.position.clone();
    }
  }

  /**
   * Change the current state of the reel.
   * @param newState - The new state to transition to.
   */
  private _changeState(newState: ReelState): void {
    if (this.currentState === newState) return;

    this.currentState = newState;
    switch (this.currentState) {
      case ReelState.IDLE:
        this._onEnterIdle();
        break;
      case ReelState.SPIN:
        this._onEnterSpin();
        break;
      case ReelState.STOP:
        if (!this.reelData.length) {
          console.warn("Try to stop reel with no data!");
          return;
        }
        this._onEnterStop();
        break;
      case ReelState.SPIN_END:
        for (let i = 0; i < GameEngine.getInstance().config.rows; i++) {
          this.symbols[i].visible = false;
        }
        break;
    }
    this.emit(this.currentState, this);
  }

  /**
   * Handle logic when entering the idle state.
   */
  private _onEnterIdle(): void {
    this.quickStop = false; // Reset quickStop flag
  }

  /**
   * Handle logic when entering the spin state.
   */
  private async _onEnterSpin(): Promise<void> {
    const promises = this.symbols
      .slice()
      .reverse()
      .map((symbol, index) => {
        return new Promise<void>((resolve) => {
          gsap.to(symbol, {
            y: this.height + symbol.y + symbol.height,
            duration: this.quickStop ? 0.1 : index * 0.1 + 0.1,
            // delay: this.quickStop ? 0 : index * 0.1, // Simulate stagger manually
            onComplete: () => {
              // symbol.visible = false; // Call the method after the animation of each symbol
              resolve(); // Resolve the promise after the tween is complete
            },
          });
        });
      });

    // Wait for all tweens to finish
    await Promise.all(promises);
    this._changeState(ReelState.SPIN_END);
  }

  /**
   * Handle logic when entering the stop state.
   */
  private async _onEnterStop(): Promise<void> {
    this.symbols.forEach((symbol: Symbol) => {
      gsap.killTweensOf(symbol);
    });
    // Set symbol types and make them visible
    for (let i = 0; i < this.reelData.length; i++) {
      this.symbols[i].setType(this.reelData[i].toString());
      this.symbols[i].visible = true;
      this.symbols[i].y = -800 + this._getY(i);
    }
    const promises = this.symbols
      .slice()
      .reverse()
      .map((symbol, index) => {
        return new Promise<void>((resolve) => {
          gsap.to(symbol, {
            y: this._getY(GameEngine.getInstance().config.rows - 1 - index),
            duration: this.quickStop ? 0.1 : 0.3,
            ease: "power2.out",
            delay: this.quickStop ? 0 : index * 0.07, // Simulate stagger manually
            onComplete: () => {
              symbol.land(); // Call the method after the animation of each symbol
              resolve(); // Resolve the promise after the tween is complete
            },
          });
        });
      });

    // Wait for all tweens to finish
    await Promise.all(promises);
    Symbol.playedExpandingWildAudio = false;

    this._changeState(ReelState.IDLE);
  }

  public getState() {
    return this.currentState;
  }
  /**
   * Get the Y position for a symbol based on its row index.
   * @param row - The row index.
   * @returns The calculated Y position.
   */
  private _getY(row: number): number {
    return Symbol.SYMBOL_HEIGHT * row + Symbol.SYMBOL_HEIGHT / 2;
  }

  /**
   * Get a symbol at a specific index.
   * @param index - The index of the symbol.
   * @returns The symbol at the given index.
   */
  public getSymbol(index: number): Symbol {
    return this.symbols[index];
  }

  public resetData() {
    this.reelData.length = 0;
  }

  /**
   * Trigger the spin state for the reel.
   */
  public triggerSpin(): void {
    this._changeState(ReelState.SPIN);
  }

  /**
   * Trigger the stop state for the reel.
   */
  public triggerStop(quickStop: boolean = false): void {
    this.quickStop = quickStop;
    switch (this.currentState) {
      case ReelState.SPIN:
        this.symbols.forEach((symbol: Symbol) => {
          gsap.killTweensOf(symbol);
        });
        this._changeState(ReelState.SPIN_END);
        break;
      case ReelState.SPIN_END:
        this._changeState(ReelState.STOP);
        break;
    }
  }

  /**
   * Move a symbol to a specific row.
   * @param symbol - The symbol to move.
   * @param row - The row index to move the symbol to.
   * @param delay - The delay before the move starts.
   * @returns A promise that resolves when the movement is complete.
   */
  public async moveSymbol(
    symbol: Symbol,
    row: number = 0,
    delay: number,
  ): Promise<void> {
    return new Promise<void>((resolve) => {
      this.symbols[row] = symbol;
      gsap.to(symbol, {
        delay,
        y: this._getY(row),
        duration: this.quickStop ? 0.1 : 0.2,
        ease: "back.out(1)",
        onComplete: () => {
          symbol.land();
          resolve();
        },
      });
    });
  }

  /**
   * Remove a symbol from a specific row.
   * @param row - The row index to remove the symbol from.
   * @param quickStop - The quickStop is for fast removal
   * @returns A promise that resolves when the symbol is removed.
   */
  public async removeSymbol(row: number, quickStop: boolean): Promise<void> {
    const symbol = this.symbols[row];
    await symbol.win(quickStop);
    this.removeChild(symbol);
    Symbol.pool.release(symbol);
  }

  public startAnticipation() { }

  public stopAnticipation() { }

  public getScattersAmount() {
    return this.reelData.filter((symbol) => symbol === "11").length;
  }
}

